import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  Users, Activity, TrendingUp, AlertCircle,
  CheckCircle2, ExternalLink, MessageSquare, Send,
  Building2, Eye, ArrowUpRight, ArrowDownRight, Minus, Calendar,
  ClipboardList, Clock, XCircle, PlusCircle, FileText,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  SYSTEM_USER_OVERVIEW,
  WEEKLY_ACTIVITY,
  ORG_ACTIVITY,
  ACCESS_INSIGHTS,
  TOP_ACCESSED_REPORTS,
  BOTTOM_ACCESSED_REPORTS,
  USER_FEEDBACK_ISSUES,
  type UserFeedbackIssue,
  type OrgActivity,
  type ProcessingLog,
  categoryLabels,
  levelLabels,
} from '@/data/admin-center-data'

export function AdminCenterPage({ version }: { version?: string }) {
  const [issues, setIssues] = useState<UserFeedbackIssue[]>(USER_FEEDBACK_ISSUES)
  const [insights, setInsights] = useState(ACCESS_INSIGHTS)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [showTransferDialog, setShowTransferDialog] = useState(false)
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)
  const [showProcessDialog, setShowProcessDialog] = useState(false)
  const [selectedIssue, setSelectedIssue] = useState<UserFeedbackIssue | null>(null)
  const [selectedInsight, setSelectedInsight] = useState<typeof ACCESS_INSIGHTS[0] | null>(null)
  const [transferTarget, setTransferTarget] = useState<'data-governance' | 'branch-direct-train'>('data-governance')
  const [feedbackText, setFeedbackText] = useState('')
  const [transferNotes, setTransferNotes] = useState('')
  const [newIssueTitle, setNewIssueTitle] = useState('')
  const [newIssueCategory, setNewIssueCategory] = useState<UserFeedbackIssue['category']>('other')
  const [newIssueDescription, setNewIssueDescription] = useState('')
  const [processLogText, setProcessLogText] = useState('')

  /* ---- 日期范围选择 ---- */
  const defaultStartDate = '2026-04-01'
  const defaultEndDate = '2026-06-05'
  const [showDateDialog, setShowDateDialog] = useState(false)
  const [dateStart, setDateStart] = useState(defaultStartDate)
  const [dateEnd, setDateEnd] = useState(defaultEndDate)

  /* ---- 按日期范围计算的数据 ---- */
  const computedData = useMemo(() => {
    const start = new Date(dateStart)
    const end = new Date(dateEnd)
    const diffMs = end.getTime() - start.getTime()
    const diffDays = Math.max(Math.floor(diffMs / (1000 * 60 * 60 * 24)), 1)
    const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
    const isMonthly = diffMonths > 3
    const periodLabel = `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 - ${end.getFullYear()}年${end.getMonth() + 1}月${end.getDate()}日`

    // 基准：约36天（8周 ≈ 56天，以WEEKLY_ACTIVITY覆盖范围为准）
    const baseDays = 56
    const ratio = diffDays / baseDays

    const overview = {
      totalUsers: SYSTEM_USER_OVERVIEW.totalUsers,
      activeUsers: Math.round(SYSTEM_USER_OVERVIEW.activeUsers * Math.min(ratio, 1)),
      newUsersThisMonth: Math.round(SYSTEM_USER_OVERVIEW.newUsersThisMonth * Math.min(ratio, 1)),
      totalReportsViewed: Math.round(SYSTEM_USER_OVERVIEW.totalReportsViewed * ratio),
      totalReportsCreated: Math.round(SYSTEM_USER_OVERVIEW.totalReportsCreated * ratio),
      avgSessionDuration: SYSTEM_USER_OVERVIEW.avgSessionDuration,
    }

    // 趋势数据：按周或按月
    if (isMonthly) {
      // 按月聚合：生成各月数据
      const monthLabels: { month: string; logins: number; reportsViewed: number; reportsCreated: number }[] = []
      let cur = new Date(start.getFullYear(), start.getMonth(), 1)
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1)
      const weekTotalLogins = WEEKLY_ACTIVITY.reduce((s, w) => s + w.logins, 0)
      const weekTotalView = WEEKLY_ACTIVITY.reduce((s, w) => s + w.reportsViewed, 0)
      const weekTotalCreated = WEEKLY_ACTIVITY.reduce((s, w) => s + w.reportsCreated, 0)
      const weeksPerMonth = 4.3
      let i = 0
      while (cur <= endMonth) {
        const mLabel = `${cur.getFullYear()}年${cur.getMonth() + 1}月`
        const monthRatio = (cur.getMonth() + 1) / 12 * 0.3 + 0.85 // slight variation
        monthLabels.push({
          month: mLabel,
          logins: Math.round(weekTotalLogins * monthRatio / weeksPerMonth),
          reportsViewed: Math.round(weekTotalView * monthRatio / weeksPerMonth),
          reportsCreated: Math.round(weekTotalCreated * monthRatio / weeksPerMonth),
        })
        cur.setMonth(cur.getMonth() + 1)
        i++
      }
      return { periodLabel, isMonthly: true, chartData: monthLabels, overview }
    } else {
      // 按周展示：截取或扩展WEEKLY_ACTIVITY
      const weekCount = Math.max(Math.ceil(diffDays / 7), 4)
      const weekData = WEEKLY_ACTIVITY.slice(0, Math.min(weekCount, WEEKLY_ACTIVITY.length))
      // 如果需要的周数超过现有数据，用最后一条数据填充
      while (weekData.length < weekCount) {
        const last = weekData[weekData.length - 1]
        weekData.push({ ...last, week: `第${18 + weekData.length}周` })
      }
      return { periodLabel, isMonthly: false, chartData: weekData, overview }
    }
  }, [dateStart, dateEnd])

  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null)

  /* ---- 机构筛选 ---- */
  const [orgLevelFilter, setOrgLevelFilter] = useState<OrgActivity['level']>('head-dept')

  const handleOpenFeedback = (issue: UserFeedbackIssue) => {
    setSelectedIssue(issue)
    setFeedbackText(issue.adminFeedback || '')
    setShowFeedbackDialog(true)
  }

  const handleSubmitFeedback = () => {
    if (!selectedIssue) return
    const now = new Date()
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    setIssues(prev =>
      prev.map(i =>
        i.id === selectedIssue.id
          ? { ...i, adminFeedback: feedbackText, feedbackAt: timeStr, status: 'in-progress' as const }
          : i
      )
    )
    setShowFeedbackDialog(false)
    setSelectedIssue(null)
    setFeedbackText('')
  }

  const handleOpenTransfer = (issue: UserFeedbackIssue, target: 'data-governance' | 'branch-direct-train') => {
    setSelectedIssue(issue)
    setTransferTarget(target)
    setTransferNotes('')
    setShowTransferDialog(true)
  }

  const handleConfirmTransfer = () => {
    if (!selectedIssue) return
    const now = new Date()
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    setIssues(prev =>
      prev.map(i =>
        i.id === selectedIssue.id
          ? { ...i, transferTarget, transferAt: timeStr, status: 'transferred' as const }
          : i
      )
    )
    setShowTransferDialog(false)
    setSelectedIssue(null)
    setTransferNotes('')
  }

  const handleSubmitNewIssue = () => {
    if (!newIssueTitle.trim() || !newIssueDescription.trim()) return
    const now = new Date()
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    const newIssue: UserFeedbackIssue = {
      id: `issue-${Date.now()}`,
      title: newIssueTitle.trim(),
      description: newIssueDescription.trim(),
      category: newIssueCategory,
      source: '管理员提交',
      reporterCount: 1,
      status: 'open',
      createdAt: timeStr,
      severity: 'medium',
    }
    setIssues(prev => [newIssue, ...prev])
    setNewIssueTitle('')
    setNewIssueCategory('other')
    setNewIssueDescription('')
    setShowSubmitDialog(false)
  }

  const nowTime = () => {
    const n = new Date()
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')} ${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`
  }

  const handleOpenProcessDialog = (insight: typeof ACCESS_INSIGHTS[0]) => {
    setSelectedInsight(insight)
    setProcessLogText('')
    setShowProcessDialog(true)
  }

  const handleStartProcessing = () => {
    if (!selectedInsight) return
    const log: ProcessingLog = {
      id: `log-${Date.now()}`,
      content: '开始跟踪处理',
      createdAt: nowTime(),
      action: 'start',
    }
    setInsights(prev =>
      prev.map(i =>
        i.id === selectedInsight.id
          ? { ...i, status: 'processing' as const, processingLogs: [...i.processingLogs, log] }
          : i
      )
    )
    setSelectedInsight({ ...selectedInsight, status: 'processing', processingLogs: [...selectedInsight.processingLogs, log] })
  }

  const handleAddProcessLog = () => {
    if (!selectedInsight || !processLogText.trim()) return
    const log: ProcessingLog = {
      id: `log-${Date.now()}`,
      content: processLogText.trim(),
      createdAt: nowTime(),
      action: 'log',
    }
    setInsights(prev =>
      prev.map(i =>
        i.id === selectedInsight.id
          ? { ...i, processingLogs: [...i.processingLogs, log] }
          : i
      )
    )
    setSelectedInsight({ ...selectedInsight, processingLogs: [...selectedInsight.processingLogs, log] })
    setProcessLogText('')
  }

  const handleCloseInsight = () => {
    if (!selectedInsight) return
    const log: ProcessingLog = {
      id: `log-${Date.now()}`,
      content: processLogText.trim() || '问题已关闭',
      createdAt: nowTime(),
      action: 'close',
    }
    setInsights(prev =>
      prev.map(i =>
        i.id === selectedInsight.id
          ? { ...i, status: 'resolved' as const, processingLogs: [...i.processingLogs, log] }
          : i
      )
    )
    setShowProcessDialog(false)
    setSelectedInsight(null)
    setProcessLogText('')
  }

  const openCount = issues.filter(i => i.status === 'open').length
  const inProgressCount = issues.filter(i => i.status === 'in-progress').length
  const transferredCount = issues.filter(i => i.status === 'transferred').length
  const resolvedCount = issues.filter(i => i.status === 'resolved').length

  const severityConfig = {
    critical: { label: '严重', dotColor: 'bg-red-500', badgeClass: 'bg-red-50 text-red-600 border-red-200' },
    high: { label: '高', dotColor: 'bg-orange-500', badgeClass: 'bg-orange-50 text-orange-600 border-orange-200' },
    medium: { label: '中', dotColor: 'bg-blue-500', badgeClass: 'bg-blue-50 text-blue-600 border-blue-200' },
    low: { label: '低', dotColor: 'bg-green-500', badgeClass: 'bg-green-50 text-green-600 border-green-200' },
  }

  const statusConfig = {
    open: { label: '待处理', className: 'bg-gray-100 text-gray-500 border-gray-200' },
    'in-progress': { label: '处理中', className: 'bg-blue-50 text-blue-600 border-blue-200' },
    transferred: { label: '已转交', className: 'bg-purple-50 text-purple-600 border-purple-200' },
    resolved: { label: '已解决', className: 'bg-green-50 text-green-600 border-green-200' },
    closed: { label: '已关闭', className: 'bg-gray-50 text-gray-400 border-gray-200' },
  }

  const categoryColors: Record<UserFeedbackIssue['category'], string> = {
    performance: 'bg-red-50 text-red-600 border-red-200',
    'data-quality': 'bg-orange-50 text-orange-600 border-orange-200',
    'feature-request': 'bg-blue-50 text-blue-600 border-blue-200',
    usability: 'bg-purple-50 text-purple-600 border-purple-200',
    bug: 'bg-amber-50 text-amber-600 border-amber-200',
    other: 'bg-gray-50 text-gray-600 border-gray-200',
  }

  const insightStatusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    unhandled: {
      label: '未处理',
      className: 'bg-gray-100 text-gray-500 border-gray-200',
      icon: <AlertCircle className="w-3 h-3" />,
    },
    processing: {
      label: '处理中',
      className: 'bg-blue-50 text-blue-600 border-blue-200',
      icon: <Clock className="w-3 h-3" />,
    },
    resolved: {
      label: '已解决',
      className: 'bg-green-50 text-green-600 border-green-200',
      icon: <CheckCircle2 className="w-3 h-3" />,
    },
  }

  const insightProcessActionLabels: Record<ProcessingLog['action'], string> = {
    start: '▶ 开始跟踪',
    log: '📝 处理记录',
    close: '■ 关闭问题',
  }

  const insightTypeConfig: Record<string, { label: string; color: string; bg: string; border: string }> = {
    'hot-slow': { label: '热点响应慢', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
    'hot-data-quality': { label: '数据质量问题', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
    'cold-low-adoption': { label: '使用率低', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    'hot-increasing': { label: '趋势上升', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    anomaly: { label: '异常访问', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  }

  return (
    <ScrollArea className="flex-1">
      <div className="p-6 bg-gray-50 min-h-full">
        {/* 系统运营概览 */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-white" />
              <h2 className="text-base font-bold text-white">系统运营概览</h2>
              <span
                className="text-xs text-white/70 ml-auto flex items-center gap-1 cursor-pointer hover:text-white transition-colors"
                onClick={() => setShowDateDialog(true)}
              >
                <Calendar className="w-3 h-3" />
                {computedData.periodLabel}
              </span>
            </div>
          </div>
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-6 gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-gcb-primary">{computedData.overview.totalUsers.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">注册用户总数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-600">{computedData.overview.activeUsers.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">本期活跃用户</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-blue-600">+{computedData.overview.newUsersThisMonth}</p>
                <p className="text-xs text-gray-500 mt-0.5">本月新增</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-purple-600">{computedData.overview.totalReportsViewed.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">报表查看总次数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-amber-600">{computedData.overview.totalReportsCreated.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">报表制作总数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-indigo-600">{computedData.overview.avgSessionDuration}</p>
                <p className="text-xs text-gray-500 mt-0.5">平均会话时长</p>
              </div>
            </div>

            {/* 活跃趋势图 */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-3.5 h-3.5 text-gcb-primary" />
                <span className="text-xs font-medium text-gray-700">{computedData.isMonthly ? '月度活跃趋势' : '周活跃趋势'}</span>
                <span className="text-[10px] text-gray-400 ml-auto flex items-center gap-3">
                  <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-blue-500 inline-block rounded"></span>登录次数</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-emerald-500 inline-block rounded"></span>报表查看</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-amber-500 inline-block rounded"></span>制作报表</span>
                </span>
              </div>
              {(() => {
                const data = computedData.chartData
                const maxLogin = Math.max(...data.map(x => x.logins))
                const maxView = Math.max(...data.map(x => x.reportsViewed))
                const maxCreated = Math.max(...data.map(x => x.reportsCreated))
                const chartH = 80
                const loginPoints = data.map((w, i) => ({
                  x: ((i + 0.5) / data.length) * 100,
                  y: chartH - (w.logins / maxLogin) * chartH * 0.95,
                }))
                const viewPoints = data.map((w, i) => ({
                  x: ((i + 0.5) / data.length) * 100,
                  y: chartH - (w.reportsViewed / maxView) * chartH * 0.95,
                }))
                const createdPoints = data.map((w, i) => ({
                  x: ((i + 0.5) / data.length) * 100,
                  y: chartH - (w.reportsCreated / maxCreated) * chartH * 0.95,
                }))
                const loginPath = loginPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                const viewPath = viewPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                const createdPath = createdPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                const labelKey = computedData.isMonthly ? 'month' : 'week'
                return (
                  <div className="relative h-32">
                    <svg viewBox={`0 0 100 ${chartH}`} className="w-full h-full" preserveAspectRatio="none">
                      <path d={loginPath} fill="none" stroke="#3b82f6" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
                      <path d={viewPath} fill="none" stroke="#10b981" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
                      <path d={createdPath} fill="none" stroke="#f59e0b" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
                      {loginPoints.map((p, i) => (
                        <circle key={`l-${i}`} cx={p.x} cy={p.y} r="1" fill="#3b82f6" stroke="#fff" strokeWidth="0.4" />
                      ))}
                      {viewPoints.map((p, i) => (
                        <circle key={`v-${i}`} cx={p.x} cy={p.y} r="1" fill="#10b981" stroke="#fff" strokeWidth="0.4" />
                      ))}
                      {createdPoints.map((p, i) => (
                        <circle key={`c-${i}`} cx={p.x} cy={p.y} r="1" fill="#f59e0b" stroke="#fff" strokeWidth="0.4" />
                      ))}
                    </svg>
                    {/* 悬浮热区 */}
                    <div className="absolute inset-0 flex">
                      {data.map((_, i) => (
                        <div
                          key={i}
                          className="flex-1 flex justify-center cursor-pointer"
                          onMouseEnter={() => setHoveredPoint(i)}
                          onMouseLeave={() => setHoveredPoint(null)}
                        >
                          <div className="w-px h-full" />
                        </div>
                      ))}
                    </div>
                    {/* 悬浮数据提示 */}
                    {hoveredPoint !== null && (
                      <div
                        className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 text-xs pointer-events-none"
                        style={{
                          left: `${((hoveredPoint + 0.5) / data.length) * 100}%`,
                          top: '4px',
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <p className="font-medium text-gray-700 mb-1">{(data[hoveredPoint] as any)[labelKey]}</p>
                        <div className="space-y-0.5">
                          <p className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                            <span className="text-gray-500">登录</span>
                            <span className="text-blue-600 font-medium ml-auto">{data[hoveredPoint].logins.toLocaleString()}</span>
                          </p>
                          <p className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                            <span className="text-gray-500">报表查看</span>
                            <span className="text-emerald-600 font-medium ml-auto">{data[hoveredPoint].reportsViewed.toLocaleString()}</span>
                          </p>
                          <p className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                            <span className="text-gray-500">制作报表</span>
                            <span className="text-amber-600 font-medium ml-auto">{data[hoveredPoint].reportsCreated.toLocaleString()}</span>
                          </p>
                        </div>
                      </div>
                    )}
                    {/* 横坐标 */}
                    <div className="absolute bottom-0 inset-x-0 flex">
                      {data.map((w, i) => (
                        <div key={i} className="flex-1 flex justify-center">
                          <span className="text-[10px] text-gray-500">{(w as any)[labelKey]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </div>
          </CardContent>
        </Card>

        {/* 机构活跃度 */}
        <Card className="mb-6">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gcb-primary" />
              <h3 className="text-sm font-semibold text-gray-900">机构活跃度</h3>
              <span className="text-xs text-gray-400 ml-auto" title="机构下活跃用户所占比例">机构活跃率 = 活跃用户数 / 机构总用户数</span>
            </div>
            {/* 级别筛选 */}
            <div className="flex items-center gap-2 mt-3">
              <span className="text-[10px] text-gray-400">筛选：</span>
              {([
                ['head-dept', '总行部门'],
                ['branch', '分行'],
                ['sub-branch', '支行'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setOrgLevelFilter(key)}
                  className={cn(
                    'text-[11px] px-2.5 py-1 rounded-md border transition-colors',
                    orgLevelFilter === key
                      ? 'bg-gcb-primary text-white border-gcb-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {[...ORG_ACTIVITY]
                .filter(d => d.level === orgLevelFilter)
                .sort((a, b) => b.activeRate - a.activeRate)
                .map((org, i) => {
                  const barWidth = (org.activeRate / 100) * 100
                  return (
                    <div key={org.id} className="flex items-center gap-4">
                      <span className="text-xs text-gray-500 w-8 text-right shrink-0">{i + 1}</span>
                      <div className="flex items-center gap-1.5 w-36 shrink-0">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">{levelLabels[org.level]}</span>
                        <span className="text-xs font-medium text-gray-700 truncate">{org.name}</span>
                      </div>
                      <div className="flex-1">
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all duration-500',
                              org.activeRate >= 90 ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' :
                              org.activeRate >= 80 ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
                              org.activeRate >= 70 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                              'bg-gradient-to-r from-red-400 to-red-500'
                            )}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-700 w-12 text-right shrink-0">{org.activeRate}%</span>
                      <span className="text-[10px] text-gray-400 w-16 text-right shrink-0 hidden sm:inline">{org.activeUsers}/{org.userCount}人</span>
                      <span className="text-[10px] text-gray-400 w-20 text-right shrink-0 hidden sm:inline">{org.reportsCreated.toLocaleString()} 份报表</span>
                    </div>
                  )
                })}
            </div>
          </CardContent>
        </Card>

        {/* 访问洞察 - v2.0 only */}
        {version !== '1' && (
        <Card className="mb-6">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <h3 className="text-sm font-semibold text-gray-900">访问洞察 — 需要关注的异常模式</h3>
              {/* 状态汇总 */}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-600">未处理 {insights.filter(i => i.status === 'unhandled').length}</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600">处理中 {insights.filter(i => i.status === 'processing').length}</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-green-50 text-green-600">已解决 {insights.filter(i => i.status === 'resolved').length}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {insights.map((insight) => {
                const typeCfg = insightTypeConfig[insight.type]
                const sCfg = insightStatusConfig[insight.status]
                return (
                  <div key={insight.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', typeCfg.bg.replace('bg-', 'bg-').replace('-50', '-500'))} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-medium text-gray-900">{insight.title}</h4>
                          <span className={cn('text-[10px] px-1.5 py-0.5 rounded border', typeCfg.color, typeCfg.bg, typeCfg.border)}>
                            {typeCfg.label}
                          </span>
                          <Badge
                            className={cn('text-[10px] h-4 px-1.5', severityConfig[insight.severity].badgeClass)}
                            variant="outline"
                          >
                            {severityConfig[insight.severity].label}
                          </Badge>
                          <Badge variant="outline" className={cn('text-[10px] h-4 px-1.5 flex items-center gap-1', sCfg.className)}>
                            {sCfg.icon}
                            {sCfg.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{insight.description}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {insight.accessCount.toLocaleString()} 次访问
                          </span>
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <Users className="w-3 h-3" /> {insight.userCount} 人
                          </span>
                          <span className={cn('text-[10px] flex items-center gap-1', insight.trend > 0 ? 'text-emerald-600' : 'text-red-500')}>
                            {insight.trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                            {Math.abs(insight.trend)}%
                          </span>
                          {insight.issueTag && (
                            <span className="text-[10px] text-red-500 bg-red-50 px-1.5 py-0.5 rounded">{insight.issueTag}</span>
                          )}
                        </div>

                        {/* 操作按钮 */}
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[10px] h-7 px-2.5"
                            onClick={() => handleOpenProcessDialog(insight)}
                          >
                            <ClipboardList className="w-3 h-3 mr-1" />
                            跟踪处理
                            {insight.processingLogs.length > 0 && (
                              <span className="ml-1 w-4 h-4 rounded-full bg-gcb-primary text-white text-[9px] flex items-center justify-center">
                                {insight.processingLogs.length}
                              </span>
                            )}
                          </Button>
                          {insight.status === 'resolved' && insight.processingLogs.length > 0 && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {insight.processingLogs.length} 条处理记录
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
        )}

        {/* 报表访问频率分析 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* 高频访问 TOP 5 */}
          <Card>
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <h3 className="text-xs font-semibold text-gray-900">高频访问 TOP 5</h3>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2.5">
                {TOP_ACCESSED_REPORTS.map((item, i) => {
                  const maxAccess = TOP_ACCESSED_REPORTS[0].accesses
                  const barW = (item.accesses / maxAccess) * 100
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-gray-700 truncate mr-2">{item.name}</span>
                        <span className="text-[10px] text-gray-500 shrink-0">{item.accesses.toLocaleString()} 次</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            i === 0 ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' :
                            i === 1 ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
                            'bg-gradient-to-r from-gray-300 to-gray-400'
                          )}
                          style={{ width: `${barW}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-400">{item.users} 人使用</span>
                        {item.trend === 'up' && <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
                        {item.trend === 'down' && <ArrowDownRight className="w-3 h-3 text-red-500" />}
                        {item.trend === 'stable' && <Minus className="w-3 h-3 text-gray-400" />}
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* 低访问量 TOP 5 */}
          <Card>
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <h3 className="text-xs font-semibold text-gray-900">低访问量 TOP 5</h3>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2.5">
                {BOTTOM_ACCESSED_REPORTS.map((item) => {
                  const maxAccess = BOTTOM_ACCESSED_REPORTS[0].accesses
                  const barW = Math.max((item.accesses / maxAccess) * 100, 5)
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-gray-700 truncate mr-2">{item.name}</span>
                        <span className="text-[10px] text-gray-500 shrink-0">{item.accesses} 次</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-300 to-amber-500 rounded-full"
                          style={{ width: `${barW}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-400">{item.users} 人</span>
                        {item.daysSinceLaunch && (
                          <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">上线 {item.daysSinceLaunch} 天</span>
                        )}
                        {item.trend === 'up' && <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
                        {item.trend === 'down' && <ArrowDownRight className="w-3 h-3 text-red-500" />}
                        {item.trend === 'stable' && <Minus className="w-3 h-3 text-gray-400" />}
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 用户反馈问题管理 */}
        <Card className="mb-6">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gcb-primary" />
                <h3 className="text-sm font-semibold text-gray-900">用户反馈问题</h3>
              </div>
              <Button size="sm" onClick={() => setShowSubmitDialog(true)} className="bg-gcb-primary hover:bg-gcb-primaryDark text-white">
                <Send className="w-3.5 h-3.5 mr-1" />
                提交反馈
              </Button>
            </div>
            {/* 状态汇总 */}
            <div className="flex items-center gap-3 mt-3">
              <span className="text-[10px] text-gray-400">状态：</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-600">待处理 {openCount}</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600">处理中 {inProgressCount}</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-purple-50 text-purple-600">已转交 {transferredCount}</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-green-50 text-green-600">已解决 {resolvedCount}</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
              {issues.map((issue) => (
                <div key={issue.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={cn('w-1.5 h-1.5 rounded-full mt-2 shrink-0', severityConfig[issue.severity].dotColor)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-medium text-gray-900">{issue.title}</h4>
                        <Badge variant="outline" className={cn('text-[10px] h-4 px-1.5', categoryColors[issue.category])}>
                          {categoryLabels[issue.category]}
                        </Badge>
                        <Badge variant="outline" className={cn('text-[10px] h-4 px-1.5', statusConfig[issue.status].className)}>
                          {statusConfig[issue.status].label}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{issue.description}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Users className="w-3 h-3" /> {issue.reporterCount} 人反馈
                        </span>
                        <span className="text-[10px] text-gray-400">{issue.source}</span>
                        <span className="text-[10px] text-gray-400">{issue.createdAt}</span>
                      </div>

                      {/* 管理员反馈 */}
                      {issue.adminFeedback && (
                        <div className="mt-3 p-3 bg-blue-50/50 rounded-md border border-blue-100">
                          <div className="flex items-center gap-1.5 mb-1">
                            <CheckCircle2 className="w-3 h-3 text-blue-600" />
                            <span className="text-[10px] font-medium text-blue-700">管理员反馈</span>
                          </div>
                          <p className="text-xs text-gray-700 leading-relaxed">{issue.adminFeedback}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{issue.feedbackAt}</p>
                        </div>
                      )}

                      {/* 转交信息 */}
                      {issue.transferTarget && (
                        <div className="mt-3 p-3 bg-purple-50/50 rounded-md border border-purple-100">
                          <div className="flex items-center gap-1.5 mb-1">
                            <ExternalLink className="w-3 h-3 text-purple-600" />
                            <span className="text-[10px] font-medium text-purple-700">已转交至 {issue.transferTarget === 'data-governance' ? '数据管控平台' : '科技直通车'}</span>
                          </div>
                          <p className="text-[10px] text-gray-400">{issue.transferAt}</p>
                        </div>
                      )}

                      {/* 操作按钮 */}
                      {issue.status === 'open' && (
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[10px] h-7 px-2.5"
                            onClick={() => handleOpenFeedback(issue)}
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            填写反馈
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[10px] h-7 px-2.5 text-orange-600 border-orange-200 hover:bg-orange-50"
                            onClick={() => handleOpenTransfer(issue, 'data-governance')}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            转交数据管控平台
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[10px] h-7 px-2.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                            onClick={() => handleOpenTransfer(issue, 'branch-direct-train')}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            转交科技直通车
                          </Button>
                        </div>
                      )}
                      {issue.status === 'in-progress' && (
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[10px] h-7 px-2.5"
                            onClick={() => handleOpenFeedback(issue)}
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            更新反馈
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 填写反馈 Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gcb-primary" />
              {selectedIssue?.adminFeedback ? '更新反馈' : '填写反馈'}
            </DialogTitle>
            {selectedIssue && (
              <DialogDescription className="text-xs">
                问题：{selectedIssue.title}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedIssue && !selectedIssue.adminFeedback && (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs text-gray-600">{selectedIssue.description}</p>
              </div>
            )}
            <Textarea
              placeholder="请输入反馈内容..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowFeedbackDialog(false); setSelectedIssue(null) }}>
              取消
            </Button>
            <Button
              onClick={handleSubmitFeedback}
              disabled={!feedbackText.trim()}
              className="bg-gcb-primary hover:bg-gcb-primaryDark text-white"
            >
              提交反馈
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 转交 Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-gcb-primary" />
              转交至 {transferTarget === 'data-governance' ? '数据管控平台' : '科技直通车'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedIssue && (
              <div className="p-3 bg-gray-50 rounded-md">
                <h4 className="text-xs font-medium text-gray-900">{selectedIssue.title}</h4>
                <p className="text-xs text-gray-500 mt-1">{selectedIssue.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className={cn('text-[10px] h-4 px-1.5', categoryColors[selectedIssue.category])}>
                    {categoryLabels[selectedIssue.category]}
                  </Badge>
                  <span className="text-[10px] text-gray-400">{selectedIssue.reporterCount} 人反馈</span>
                </div>
              </div>
            )}
            <Textarea
              placeholder="转交备注（可选）..."
              value={transferNotes}
              onChange={(e) => setTransferNotes(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <p className="text-[10px] text-gray-400">
              {transferTarget === 'data-governance'
                ? '转交数据管控平台后，该问题将由数据质量团队处理。'
                : '转交科技直通车后，该问题将由科技团队处理。'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTransferDialog(false); setSelectedIssue(null) }}>
              取消
            </Button>
            <Button
              onClick={handleConfirmTransfer}
              className="bg-gcb-primary hover:bg-gcb-primaryDark text-white"
            >
              确认转交
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 提交新问题 Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-4 h-4 text-gcb-primary" />
              提交反馈问题
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">问题标题</label>
              <Input
                placeholder="请输入问题标题..."
                value={newIssueTitle}
                onChange={(e) => setNewIssueTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">问题分类</label>
              <div className="flex flex-wrap gap-2">
                {([
                  ['performance', '性能问题'],
                  ['data-quality', '数据质量'],
                  ['feature-request', '功能建议'],
                  ['usability', '使用体验'],
                  ['bug', '缺陷'],
                  ['other', '其他'],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setNewIssueCategory(key)}
                    className={cn(
                      'text-[11px] px-2.5 py-1 rounded-md border transition-colors',
                      newIssueCategory === key
                        ? 'bg-gcb-primary text-white border-gcb-primary'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">问题描述</label>
              <Textarea
                placeholder="请详细描述问题..."
                value={newIssueDescription}
                onChange={(e) => setNewIssueDescription(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSubmitDialog(false); setNewIssueTitle(''); setNewIssueDescription('') }}>
              取消
            </Button>
            <Button
              onClick={handleSubmitNewIssue}
              disabled={!newIssueTitle.trim() || !newIssueDescription.trim()}
              className="bg-gcb-primary hover:bg-gcb-primaryDark text-white"
            >
              提交
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 跟踪处理 Dialog */}
      <Dialog open={showProcessDialog} onOpenChange={(open) => { if (!open) { setShowProcessDialog(false); setSelectedInsight(null); setProcessLogText('') } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-gcb-primary" />
              跟踪处理 — {selectedInsight?.title}
            </DialogTitle>
            {selectedInsight && (
              <DialogDescription className="text-xs">
                <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px]', insightStatusConfig[selectedInsight.status].className)}>
                  {insightStatusConfig[selectedInsight.status].icon}
                  {insightStatusConfig[selectedInsight.status].label}
                </span>
                {selectedInsight.processingLogs.length > 0 && (
                  <span className="ml-2 text-gray-400">{selectedInsight.processingLogs.length} 条记录</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>

          {/* 处理日志列表 */}
          <ScrollArea className="flex-1 max-h-64 min-h-0">
            <div className="space-y-3 pr-4 py-2">
              {selectedInsight?.processingLogs.length === 0 && (
                <div className="text-center py-6 text-gray-400 text-xs">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  暂无处理记录，点击"开始处理"启动跟踪
                </div>
              )}
              {selectedInsight?.processingLogs.map((log, idx) => (
                <div key={log.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center shrink-0',
                      log.action === 'start' ? 'bg-blue-100' :
                      log.action === 'close' ? 'bg-green-100' : 'bg-gray-100'
                    )}>
                      {log.action === 'start' && <PlusCircle className="w-3 h-3 text-blue-600" />}
                      {log.action === 'log' && <MessageSquare className="w-3 h-3 text-gray-500" />}
                      {log.action === 'close' && <XCircle className="w-3 h-3 text-green-600" />}
                    </div>
                    {idx < (selectedInsight.processingLogs.length - 1) && (
                      <div className="w-px h-full bg-gray-200 mt-1" style={{ minHeight: '16px' }} />
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-medium text-gray-500">
                        {insightProcessActionLabels[log.action]}
                      </span>
                      <span className="text-[10px] text-gray-400">{log.createdAt}</span>
                    </div>
                    {log.action !== 'start' && log.action !== 'close' && (
                      <p className="text-xs text-gray-700 leading-relaxed">{log.content}</p>
                    )}
                    {log.action === 'close' && (
                      <p className="text-xs text-gray-700 leading-relaxed">{log.content !== '问题已关闭' ? log.content : null}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* 输入区域 */}
          <div className="border-t pt-3 shrink-0">
            {selectedInsight?.status === 'processing' ? (
              <div className="space-y-2">
                <Textarea
                  placeholder="输入处理记录..."
                  value={processLogText}
                  onChange={(e) => setProcessLogText(e.target.value)}
                  rows={2}
                  className="resize-none text-xs"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleAddProcessLog}
                    disabled={!processLogText.trim()}
                    className="bg-gcb-primary hover:bg-gcb-primaryDark text-white text-xs h-7"
                  >
                    <PlusCircle className="w-3 h-3 mr-1" />
                    添加记录
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCloseInsight}
                    className="text-xs h-7 px-3 text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    关闭问题
                  </Button>
                </div>
              </div>
            ) : selectedInsight?.status === 'resolved' ? (
              <div className="text-center py-2">
                <span className="text-xs text-green-600 flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> 问题已关闭
                </span>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={handleStartProcessing}
                className="bg-gcb-primary hover:bg-gcb-primaryDark text-white text-xs h-7 w-full"
              >
                <PlusCircle className="w-3 h-3 mr-1" />
                开始处理
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 日期范围选择 Dialog */}
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gcb-primary" />
              选择时间范围
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">开始日期</label>
                <Input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  max={dateEnd}
                  min="2024-06-01"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">结束日期</label>
                <Input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  min={dateStart}
                />
              </div>
            </div>
            <p className="text-[10px] text-gray-400">
              时间范围超过3个月时按月度展示趋势，最短7天，最长2年。
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDateStart(defaultStartDate); setDateEnd(defaultEndDate) }}>
              恢复默认
            </Button>
            <Button
              onClick={() => setShowDateDialog(false)}
              className="bg-gcb-primary hover:bg-gcb-primaryDark text-white"
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollArea>
  )
}
